import React from 'react';

import { Typography } from '@/components/Typography';

import * as S from './StateStepper.styles';

interface StateStepperProps {
  activeStep: number;
  children?: React.ReactNode;
  isFullWidth?: boolean;
  isVertical?: boolean;
}

const StateStepper = ({
  activeStep,
  children,
  isFullWidth = false,
  isVertical = false,
}: StateStepperProps) => {
  const childrenWithProps = React.Children.map(children, (child, index) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        isActive: index === activeStep,
        isCompleted: index < activeStep,
        isLast: index === React.Children.count(children) - 1,
        isVertical: isVertical,
        isFullWidth: isFullWidth,
      } as React.PropsWithChildren<StepProps>);
    }
  });

  return (
    <S.StepperWrapper
      data-testid='StateStepperWrapper'
      flexDirection={isVertical ? 'column' : 'row'}
    >
      {isVertical ? childrenWithProps?.reverse() : childrenWithProps}
    </S.StepperWrapper>
  );
};

interface LabelProps {
  children?: React.ReactNode;
  isActive?: boolean;
  isCompleted?: boolean;
  isVertical?: boolean;
}

const Label = ({ children, isActive, isCompleted, isVertical }: LabelProps) => (
  <Typography
    color={isActive || isCompleted ? 'secondary' : 'secondaryGrey'}
    data-test-id='StateStepperLabel'
    fontWeight={isActive ? 'bold' : 'medium'}
    ml={isVertical ? 's125' : 's000'}
    scale={isVertical ? 'p2' : 'p3'}
  >
    {children}
  </Typography>
);

interface DateLabelProps {
  children?: React.ReactNode;
  isVertical?: boolean;
}

const DateLabel = ({ children, isVertical }: DateLabelProps) => (
  <Typography
    color='secondaryGrey'
    data-test-id='StateStepperDateLabel'
    ml={isVertical ? 's125' : 's000'}
    scale='p3'
  >
    {children}
  </Typography>
);

interface StepProps {
  children?: React.ReactNode;
  isActive?: boolean;
  isCompleted?: boolean;
  isFullWidth?: boolean;
  isLast?: boolean;
  isVertical?: boolean;
}

const Step = ({
  children,
  isActive = true,
  isCompleted = true,
  isFullWidth = false,
  isLast = false,
  isVertical = false,
}: StepProps) => {
  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        isVertical,
        isActive,
        isCompleted,
      } as React.PropsWithChildren<LabelProps>);
    }
  });

  return (
    <S.StepContainer
      data-testid='StateStepperStep'
      isActive={isActive}
      isCompleted={isCompleted}
      isFullWidth={isFullWidth}
      vertical={isVertical}
    >
      <S.StepBadge
        data-testid='StateStepperBadge'
        isActive={isActive}
        isCompleted={isCompleted}
      >
        {isCompleted && <S.CheckMark />}
      </S.StepBadge>
      <S.TextContainer vertical={isVertical}>
        <Typography
          as='div'
          color={isLast ? 'secondaryGrey' : 'secondary'}
          data-testid={
            isCompleted ? 'StateStepperCompletedLabel' : 'StateStepperLabel'
          }
          scale='p3'
          textAlign={isVertical ? 'left' : 'center'}
        >
          {childrenWithProps}
        </Typography>
      </S.TextContainer>
    </S.StepContainer>
  );
};

StateStepper.Step = Step;
StateStepper.Label = Label;
StateStepper.DateLabel = DateLabel;

export default StateStepper;
